import React, { RefObject } from 'react';

import Check from '@rover/icons/svg/check.svg';
import { Box, Flex, Paragraph, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

import FrequentlyAskedQuestions, { FrequentlyAskedQuestion } from './FrequentlyAskedQuestions';

/* TODO (DEV-104195): Translate */

type RequirementsProps = {
  description: React.ReactNode;
};

const Requirements = ({ description }: RequirementsProps): JSX.Element => {
  const { colors } = useTheme();
  return (
    <Flex gap="2x" alignItems="center" py="4x">
      <Box>
        <Check height="24px" width="24px" fill={colors.green['500']} />
      </Box>
      <Flex flex="1">
        <Paragraph size="100" mb="0x">
          {description}
        </Paragraph>
      </Flex>
    </Flex>
  );
};

const REQUIREMENTS: RequirementsProps[] = [
  {
    description: (
      <>
        Pass a third-party{' '}
        <Text size="100" fontWeight="semibold">
          background check
        </Text>
      </>
    ),
  },
  {
    description: (
      <>
        Show{' '}
        <Text size="100" fontWeight="semibold">
          proof of education or certification
        </Text>{' '}
        in dog training
      </>
    ),
  },
  {
    description: (
      <>
        Commit to{' '}
        <Text size="100" fontWeight="semibold">
          rewards-based training
        </Text>
      </>
    ),
  },
];

type TrainingFaqsProps = {
  isPuppy?: boolean;
  backToTopStartingElementId?: string;
  faqRef?: RefObject<HTMLDivElement | null>;
  isFirstQuestionOpen?: boolean;
} & Omit<React.ComponentProps<typeof FrequentlyAskedQuestions>, 'data-testid' | 'htmlRef'>;

export default function TrainingFaqs({
  isPuppy = false,
  backToTopStartingElementId,
  faqRef,
  isFirstQuestionOpen,
  ...rest
}: TrainingFaqsProps): JSX.Element {
  const petType = isPuppy ? 'puppy' : 'dog';
  const petTypeCapitalized = isPuppy ? 'Puppy' : 'Dog';

  return (
    <FrequentlyAskedQuestions data-testid="training-faqs" htmlRef={faqRef} {...rest}>
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        FAQs
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        Frequently asked questions about {petTypeCapitalized} Training on Rover
      </FrequentlyAskedQuestions.Subheader>
      {/* 1 */}
      <FrequentlyAskedQuestion
        question="How do trainers get approved to list on Rover?"
        isOpen={isFirstQuestionOpen}
      >
        <>
          <Paragraph size="100" webProps={{ maxWidth: 'none' }}>
            To list on Rover, trainers must:
          </Paragraph>
          {REQUIREMENTS.map((requirementsProps, index) => (
            <Requirements key={index} {...requirementsProps} />
          ))}
        </>
      </FrequentlyAskedQuestion>

      {/* 2 */}
      <FrequentlyAskedQuestion
        question="What is rewards-based training?"
        id={backToTopStartingElementId}
      >
        <>
          <Paragraph webProps={{ maxWidth: 'none' }}>
            {isPuppy
              ? 'Rewards-based training encourages desired behaviors in puppies by reinforcing the behavior with something the puppy likes; treats, play, or toys. Rewards-based training prioritizes using the least intrusive intervention; it promotes thinking rather than punishing mistakes.'
              : 'Rewards-based training encourages dogs to offer desired behaviors by reinforcing the behavior with something the dog likes, including treats, play, or toys. Rewards-based training prioritizes using the least intrusive intervention; it promotes thinking rather than punishing mistakes.'}
          </Paragraph>
          <Paragraph webProps={{ maxWidth: 'none' }}>
            When rewards are used, the likelihood the behavior is repeated increases. The positive
            connection to the reward results in making learning enjoyable, strengthens the
            human-animal bond and over time leads to consistent desired behaviors.
          </Paragraph>
        </>
      </FrequentlyAskedQuestion>

      {/* 3 */}
      <FrequentlyAskedQuestion question="Where do training sessions happen?">
        <>
          <Paragraph webProps={{ maxWidth: 'none' }}>
            Training can happen many places — your home, a park, a store, or anywhere else that you
            and your trainer decide is best for the training you are doing! Many trainers will
            recommend training in your home. This allows them to observe your {petType} in their
            natural environment so they can effectively assess and best help reach your training
            goals.
          </Paragraph>
          <Paragraph webProps={{ maxWidth: 'none' }}>
            Trainers will often mention on their profile where they typically hold training
            sessions, but you can always contact a trainer to learn more!
          </Paragraph>
        </>
      </FrequentlyAskedQuestion>

      {/* 4 */}
      <FrequentlyAskedQuestion question="What if I decide the trainer I contact is not the right fit?">
        <Paragraph webProps={{ maxWidth: 'none' }}>
          No problem! When you initially contact a trainer, you will be prompted to select a
          proposed first session date — this is not final, and is confirmed only if both parties
          decide to move forward. We also recommend contacting multiple trainers to make sure you
          find a trainer that is the right fit for you and your {isPuppy ? 'puppy' : 'pet'}.
        </Paragraph>
      </FrequentlyAskedQuestion>

      {/* 5 */}
      <FrequentlyAskedQuestion question="Can I contact multiple trainers?">
        <>
          <Paragraph webProps={{ maxWidth: 'none' }}>
            Of course! Having a trainer that is the right fit for both you and your pup is an
            important step to reaching your training goals. We recommend reaching out to any trainer
            that interests you. Your initial request is not final until both you and the trainer
            decide you want to move forward with booking.
          </Paragraph>
          <Paragraph webProps={{ maxWidth: 'none' }}>
            Also, trainers may offer or require consultations. Make sure to ask trainers so you can
            make the best decision for you and your {isPuppy ? 'puppy' : 'pet'}.
          </Paragraph>
        </>
      </FrequentlyAskedQuestion>

      {/* 6 */}
      <FrequentlyAskedQuestion
        question={`My ${petType} has advanced training needs. Can trainers on Rover help me?`}
      >
        <>
          <Paragraph webProps={{ maxWidth: 'none' }}>
            {isPuppy
              ? 'Many trainers on Rover indicate experience in a wide range of behaviors, from general puppy training, like potty training, crate training and basic cues, to more advanced behaviors and training types.'
              : 'Many trainers on Rover indicate experience in a wide range of behaviors, from puppy training to more advanced behaviors and training types.'}
          </Paragraph>
          <Paragraph webProps={{ maxWidth: 'none' }}>
            Review a trainer's profile to see if their experience aligns with your needs, and if you
            have questions, don't hesitate to contact them to ensure they're the right trainer for
            you and your {isPuppy ? 'puppy' : 'pet'}.
          </Paragraph>
        </>
      </FrequentlyAskedQuestion>
    </FrequentlyAskedQuestions>
  );
}
